import { FetchResult, NextLink, Observable, Operation, ServerParseError } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { LocationPaths } from 'location.types'
import { AUTH_TOKEN_HEADER, clearAuthToken } from './authTokenLink'
import { MFA_TOKEN_HEADER, clearMfaToken, getMfaToken } from './mfaTokenLink'
import { endWebSession, isWebSessionAccessToken, renewWebSessionAccessToken } from './webSession'

const redirectToLoginErrorCodes = [401, 403]

// The backend rejects requests whose MFA token is missing, expired or revoked
// with this GraphQL error (in a 200 response), not with a 401.
export const INVALID_MFA_TOKEN_ERROR = 'invalid_mfa_token'

const PUBLIC_PATHS: string[] = [LocationPaths.Login, LocationPaths.ResetPasswordRequest]

const onPublicPath = () => PUBLIC_PATHS.includes(window.location.pathname)

const navigateToLogin = () => {
  if (onPublicPath()) return

  window.location.href = LocationPaths.Login
}

// Credentials live in localStorage, shared by every tab: a rejection that
// arrives after another tab stored a new MFA token must not clear it.
const sentCurrentMfaToken = (operation: Operation) =>
  (operation.getContext().headers?.[MFA_TOKEN_HEADER] ?? null) === getMfaToken()

export const isInvalidMfaTokenError = (errors?: ReadonlyArray<{ message: string }>) =>
  !!errors?.some(({ message }) => message === INVALID_MFA_TOKEN_ERROR)

const signOut = () => {
  clearAuthToken()
  clearMfaToken()
  void endWebSession()
  navigateToLogin()
}

const isAuthenticationFailure = (error?: unknown) =>
  redirectToLoginErrorCodes.includes((error as ServerParseError | undefined)?.statusCode ?? 0)

const sentAccessToken = (operation: Operation): string | undefined =>
  operation.getContext().headers?.[AUTH_TOKEN_HEADER]?.replace(/^Bearer /, '')

const handleInvalidMfaToken = (operation: Operation, errors?: FetchResult['errors']) => {
  if (!isInvalidMfaTokenError(errors) || !sentCurrentMfaToken(operation)) return

  // On the login screen the admin may be mid-challenge, so keep their auth
  // token there; AuthProvider moves the sign-in on to the SMS challenge.
  if (onPublicPath()) {
    clearMfaToken()
    return
  }

  // A web session that hasn't passed MFA may be a sign-in waiting at the SMS
  // challenge in another tab, which shares its cookie. Ending it would sign
  // that tab out too, so only this tab goes to the login screen.
  if (isWebSessionAccessToken(sentAccessToken(operation))) {
    navigateToLogin()
    return
  }

  signOut()
}

// A web session's access token can expire or be revoked before its scheduled
// renewal; retry once with a refreshed token before signing out. The retry's
// response skips this link, so its authentication failures are handled here.
const retryWithRenewedAccessToken = (
  operation: Operation,
  forward: NextLink,
  rejectedToken: string,
  error: Error
) =>
  new Observable<any>((observer) => {
    let subscription: { unsubscribe: () => void } | undefined
    let cancelled = false
    renewWebSessionAccessToken(rejectedToken).then(
      (token) => {
        if (cancelled) return
        if (!token) {
          signOut()
          observer.error(error)
          return
        }

        operation.setContext(({ headers = {} }: Record<string, any>) => ({
          webSessionRetried: true,
          headers: { ...headers, [AUTH_TOKEN_HEADER]: `Bearer ${token}` }
        }))
        subscription = forward(operation).subscribe({
          next: (result) => {
            handleInvalidMfaToken(operation, result.errors)
            observer.next(result)
          },
          error: (retryError) => {
            if (isAuthenticationFailure(retryError)) signOut()
            observer.error(retryError)
          },
          complete: () => observer.complete()
        })
      },
      (refreshError) => {
        if (!cancelled) observer.error(refreshError)
      }
    )

    return () => {
      cancelled = true
      subscription?.unsubscribe()
    }
  })

export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (isAuthenticationFailure(networkError)) {
    const rejectedToken = sentAccessToken(operation)
    if (
      (networkError as ServerParseError).statusCode === 401 &&
      isWebSessionAccessToken(rejectedToken) &&
      !operation.getContext().webSessionRetried
    ) {
      return retryWithRenewedAccessToken(operation, forward, rejectedToken!, networkError!)
    }

    signOut()
    return undefined
  }

  handleInvalidMfaToken(operation, graphQLErrors)
  return undefined
})
